// Reports screen -- presentation layer only. Every number on this screen
// comes from the same functions the old plain-table Reports used
// (computeTotals / expenseCategoryBreakdown / monthlyBreakdown /
// filterByDateRange from reports.js and pnl.js) and the same site-scoped
// state (state.recentTransactions / materialRecords / labourRecords) that
// sites.js's selectSite() already refreshes on every site switch. No new
// data source, no new calculation, no schema change -- this file only
// changes how the existing numbers are laid out.

import { getState, setState } from '../state/store.js';
import { getSelectedSite, openSitePicker } from '../sites/sites.js';
import { money, formatDate, todayISO, monthBounds } from './format.js';
import { computeTotals, expenseCategoryBreakdown } from '../pnl/pnl.js';
import { filterByDateRange, monthlyBreakdown, cumulativeMonthlyBreakdown, monthLabel, toCSV, downloadCSV } from '../reports/reports.js';
import { openFormSheet, closeFormSheet } from './modals.js';

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (m) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;'
  }[m]));
}

// Fixed palette the category donut cycles through -- independent of category
// count (categories are whatever users have typed as expense categories, not
// a fixed enum), so this never runs out of colors, it just repeats.
const CHART_COLORS = ['#e33f43', '#2774d8', '#b45309', '#07934f', '#7c3aed', '#6b7280'];
const INCOME_COLOR = '#07934f';
const EXPENSE_COLOR = '#e33f43';
const TX_PAGE_SIZE = 10;

const REPORT_DEFS = [
  { key: 'overview', label: 'Overview', tab: 'Overview', desc: 'Summary, charts and recent activity' },
  { key: 'income', label: 'Income Report', tab: 'Income', desc: 'All income transactions' },
  { key: 'expense', label: 'Expense Report', tab: 'Expense', desc: 'All expense transactions' },
  { key: 'category', label: 'Expense by Category', tab: 'Category', desc: 'Expense totals grouped by category' },
  { key: 'transactions', label: 'Detailed Transactions', tab: 'Transactions', desc: 'Every income and expense transaction in this range' },
  { key: 'monthly', label: 'Monthly P&L', tab: 'Monthly', desc: 'Month-by-month breakdown' },
  { key: 'material', label: 'Material Report', tab: 'Materials', desc: 'Material purchases' },
  { key: 'labour', label: 'Labour Report', tab: 'Labour', desc: 'Labour payments' }
];

function labourBasisValue(l) {
  if (l.basis === 'daily') return `${l.days ?? ''} days`;
  if (l.basis === 'sqft') return `${l.sqft ?? ''} sqft`;
  return money(l.contract_amount || 0);
}
function labourRateDisplay(l) {
  if (l.basis === 'daily') return money(l.daily_rate);
  if (l.basis === 'sqft') return money(l.sqft_rate);
  return '—';
}
function labourRateRaw(l) {
  if (l.basis === 'daily') return l.daily_rate ?? '';
  if (l.basis === 'sqft') return l.sqft_rate ?? '';
  return '';
}

function periodLabel(state) {
  if (!state.reportDateFrom && !state.reportDateTo) return 'All Time';
  return `${formatDate(state.reportDateFrom) || '…'} – ${formatDate(state.reportDateTo) || '…'}`;
}

// ---------------------------------------------------------------------------
// Header: icon/title, site + date-range pills (open the existing site picker
// sheet / a new date-range sheet), Export CSV for whichever tab is active.
// ---------------------------------------------------------------------------
function reportsHeader(state) {
  const site = getSelectedSite();
  return `
    <div class="reportsHeader">
      <div class="reportsHeaderTop">
        <div class="reportsIconBadge">📊</div>
        <div>
          <h2>Reports</h2>
          <div class="reportsSubtitle">Client financial report</div>
        </div>
      </div>
      <div class="reportsFilterBar">
        <button type="button" class="reportsPill" id="reportsSitePill">
          <span>🏢 ${site ? escapeHtml(site.name) : 'Select site'}</span><span class="pillChevron">▾</span>
        </button>
        <button type="button" class="reportsPill" id="reportsDatePill">
          <span>📅 ${escapeHtml(periodLabel(state))}</span><span class="pillChevron">▾</span>
        </button>
        <button type="button" id="reportExportBtn" class="dBtn primary">⬇ Export CSV</button>
      </div>
    </div>
  `;
}

function openDateRangeSheet() {
  const state = getState();
  const body = document.createElement('div');
  body.className = 'dateRangeSheetBody';
  body.innerHTML = `
    <div class="reportPeriodButtons">
      <button type="button" data-period="today">Today</button>
      <button type="button" data-period="month">This Month</button>
      <button type="button" data-period="all">All Time</button>
    </div>
    <label>From <input type="date" id="reportDateFromInput" value="${state.reportDateFrom || ''}"></label>
    <label>To <input type="date" id="reportDateToInput" value="${state.reportDateTo || ''}"></label>
  `;

  body.querySelectorAll('[data-period]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const p = btn.dataset.period;
      if (p === 'today') {
        const t = todayISO();
        setState({ reportDateFrom: t, reportDateTo: t, reportsTxPage: 1 });
      } else if (p === 'month') {
        const { first, last } = monthBounds();
        setState({ reportDateFrom: first, reportDateTo: last, reportsTxPage: 1 });
      } else if (p === 'all') {
        setState({ reportDateFrom: '', reportDateTo: '', reportsTxPage: 1 });
      }
      closeFormSheet();
    });
  });
  body.querySelector('#reportDateFromInput').addEventListener('change', (e) => {
    setState({ reportDateFrom: e.target.value, reportsTxPage: 1 });
  });
  body.querySelector('#reportDateToInput').addEventListener('change', (e) => {
    setState({ reportDateTo: e.target.value, reportsTxPage: 1 });
  });

  openFormSheet('Date Range', body);
}

// ---------------------------------------------------------------------------
// Financial summary card -- KPI grid (always reflects current site + date
// filter, independent of which tab is open) plus the quotation/received/
// outstanding row (site's quotation_amount vs income received in range).
// ---------------------------------------------------------------------------
function kpiCardsHtml(totals) {
  return `
    <div class="reportsKpiGrid">
      <div class="kpiCard income">
        <div class="kpiIconCircle income">↗</div>
        <small>Total Income</small>
        <b>${money(totals.income)}</b>
      </div>
      <div class="kpiCard expense">
        <div class="kpiIconCircle expense">↘</div>
        <small>Total Expense</small>
        <b>${money(totals.expense)}</b>
      </div>
      <div class="kpiCard profit">
        <div class="kpiIconCircle profit">📶</div>
        <small>Net Profit</small>
        <b class="${totals.profit >= 0 ? 'green' : 'red'}">${money(totals.profit)}</b>
      </div>
      <div class="kpiCard margin">
        <div class="kpiIconCircle margin">%</div>
        <small>Profit %</small>
        <b>${totals.profitPct.toFixed(1)}%</b>
      </div>
    </div>
  `;
}

function quotationRowHtml(site, totals) {
  if (!site) return '';
  const quotation = Number(site.quotation_amount) || 0;
  const received = totals.income;
  const outstanding = quotation - received;
  const receivedPct = quotation > 0 ? (received / quotation) * 100 : 0;
  const outstandingPct = quotation > 0 ? (outstanding / quotation) * 100 : 0;
  return `
    <div class="reportsQuotationGrid">
      <div class="quotaCard">
        <div class="kpiIconCircle quota">📄</div>
        <div><small>Total Quotation</small><b>${money(quotation)}</b></div>
      </div>
      <div class="quotaCard">
        <div class="kpiIconCircle received">✓</div>
        <div><small>Amount Received</small><b>${money(received)}</b><span class="quotaPct green">${receivedPct.toFixed(1)}%</span></div>
      </div>
      <div class="quotaCard">
        <div class="kpiIconCircle outstanding">⏳</div>
        <div><small>Outstanding</small><b>${money(outstanding)}</b><span class="quotaPct amber">${outstandingPct.toFixed(1)}%</span></div>
      </div>
    </div>
  `;
}

function financialSummarySection(tx) {
  const totals = computeTotals(tx);
  const site = getSelectedSite();
  return `
    <div class="financialSummaryCard">
      <div class="reportsSummaryHead"><span class="summaryHeadIcon">📋</span><h3>Project Financial Summary</h3></div>
      ${kpiCardsHtml(totals)}
      ${quotationRowHtml(site, totals)}
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Tab bar -- replaces the old card-grid hub. Horizontally scrollable so it
// stays usable at 360px without wrapping or shrinking labels unreadably.
// ---------------------------------------------------------------------------
function tabBar(activeKey) {
  return `
    <div class="reportsTabBarWrap">
      <div class="reportsTabBar">
        ${REPORT_DEFS.map((d) => `
          <button type="button" class="${d.key === activeKey ? 'active' : ''}" data-open-report="${d.key}" title="${escapeHtml(d.desc)}">${escapeHtml(d.tab)}</button>
        `).join('')}
      </div>
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Charts -- lightweight, no external library, built from data the existing
// functions already compute (monthlyBreakdown / cumulativeMonthlyBreakdown /
// expenseCategoryBreakdown).
// ---------------------------------------------------------------------------
function monthlyTrendChart(rows) {
  if (!rows.length) return '';
  const max = Math.max(1, ...rows.map((r) => Math.max(r.income, r.expense)));
  return `
    <div class="chartCard">
      <h3>Monthly Trend</h3>
      <div class="monthlyTrendScroll">
        <div class="monthlyTrendRow">
          ${rows.map((r) => `
            <div class="monthlyTrendCol">
              <div class="monthlyTrendBars" title="${escapeHtml(monthLabel(r.month))}: Income ${money(r.income)}, Expense ${money(r.expense)}">
                <div class="monthlyTrendBar income" style="height:${Math.max(2, (r.income / max) * 100)}%"></div>
                <div class="monthlyTrendBar expense" style="height:${Math.max(2, (r.expense / max) * 100)}%"></div>
              </div>
              <div class="monthlyTrendLabel">${escapeHtml(monthLabel(r.month))}</div>
            </div>
          `).join('')}
        </div>
      </div>
      <div class="chartLegend">
        <span><i class="legendDot income"></i> Income</span>
        <span><i class="legendDot expense"></i> Expense</span>
      </div>
    </div>
  `;
}

// Classic stroke-dasharray donut: one <circle> per category, each dashed to
// its share of the circumference and rotated to the previous segment's end.
export function categoryDonutChart(rows, totalLabel) {
  const total = rows.reduce((s, r) => s + r.amount, 0) || 1;
  const r = 60, cx = 70, cy = 70, circumference = 2 * Math.PI * r;
  let offset = 0;
  const segments = rows.map((row, i) => {
    const dash = (row.amount / total) * circumference;
    const circle = `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="${CHART_COLORS[i % CHART_COLORS.length]}" stroke-width="20" stroke-dasharray="${dash} ${circumference - dash}" stroke-dashoffset="${-offset}" transform="rotate(-90 ${cx} ${cy})"><title>${escapeHtml(row.category)}: ${money(row.amount)}</title></circle>`;
    offset += dash;
    return circle;
  }).join('');

  return `
    <div class="donutWrap">
      <svg viewBox="0 0 140 140" class="donutSvg">${segments}</svg>
      <div class="donutCenter"><b>${money(total)}</b><small>${escapeHtml(totalLabel)}</small></div>
    </div>
    <div class="donutLegend">
      ${rows.map((row, i) => `
        <button type="button" class="donutLegendRow" data-report-category="${escapeHtml(row.category)}">
          <span class="legendDot" style="background:${CHART_COLORS[i % CHART_COLORS.length]}"></span>
          <span class="legendInfo">
            <b class="legendCat">${escapeHtml(row.category)}</b>
            <small><span class="legendAmt">${money(row.amount)}</span><span class="legendPct">${((row.amount / total) * 100).toFixed(1)}%</span></small>
          </span>
          <span class="legendChevron">›</span>
        </button>
      `).join('')}
    </div>
  `;
}

function categoryBreakdownCard(rows) {
  return `
    <div class="chartCard">
      <h3>Expense Breakdown</h3>
      ${rows.length ? categoryDonutChart(rows, 'Total Expense') : '<div class="empty">No expenses in this range.</div>'}
    </div>
  `;
}

// Cumulative income/expense line chart -- plots the running total per month
// so the two lines show how income and expense accumulate across the range,
// not just each month's isolated figure (that's what Monthly Trend is for).
function cumulativeTrendChart(tx) {
  const rows = cumulativeMonthlyBreakdown(tx);
  if (!rows.length) return '<div class="empty">No data in this range.</div>';

  const w = 600, h = 200, padL = 4, padR = 4, padT = 10, padB = 10;
  const maxVal = Math.max(1, ...rows.map((r) => Math.max(r.cumIncome, r.cumExpense)));
  const stepX = rows.length > 1 ? (w - padL - padR) / (rows.length - 1) : 0;
  const xFor = (i) => padL + stepX * i;
  const yFor = (v) => padT + (1 - v / maxVal) * (h - padT - padB);
  const baseY = yFor(0);

  const incomePoints = rows.map((r, i) => `${xFor(i)},${yFor(r.cumIncome)}`).join(' ');
  const expensePoints = rows.map((r, i) => `${xFor(i)},${yFor(r.cumExpense)}`).join(' ');
  const lastX = xFor(rows.length - 1);
  const incomeArea = `${padL},${baseY} ${incomePoints} ${lastX},${baseY}`;
  const expenseArea = `${padL},${baseY} ${expensePoints} ${lastX},${baseY}`;

  const dots = (color, valueOf) => rows.map((r, i) =>
    `<circle cx="${xFor(i)}" cy="${yFor(valueOf(r))}" r="3.5" fill="${color}"><title>${escapeHtml(monthLabel(r.month))}: ${money(valueOf(r))}</title></circle>`
  ).join('');

  return `
    <div class="trendLineWrap">
      <svg viewBox="0 0 ${w} ${h}" class="trendLineSvg" preserveAspectRatio="none">
        <polygon points="${incomeArea}" fill="${INCOME_COLOR}" opacity="0.12"></polygon>
        <polygon points="${expenseArea}" fill="${EXPENSE_COLOR}" opacity="0.12"></polygon>
        <polyline points="${incomePoints}" fill="none" stroke="${INCOME_COLOR}" stroke-width="2.5"></polyline>
        <polyline points="${expensePoints}" fill="none" stroke="${EXPENSE_COLOR}" stroke-width="2.5"></polyline>
        ${dots(INCOME_COLOR, (r) => r.cumIncome)}
        ${dots(EXPENSE_COLOR, (r) => r.cumExpense)}
      </svg>
      <div class="trendAxisLabels">${rows.map((r) => `<span>${escapeHtml(monthLabel(r.month))}</span>`).join('')}</div>
    </div>
    <div class="chartLegend">
      <span><i class="legendDot income"></i> Income</span>
      <span><i class="legendDot expense"></i> Expense</span>
    </div>
  `;
}

function trendChartCard(tx) {
  return `
    <div class="chartCard">
      <h3>Income vs Expense (Cumulative)</h3>
      ${cumulativeTrendChart(tx)}
    </div>
  `;
}

function combinedTxRow(t) {
  const isIncome = t.type === 'income';
  return `
    <div class="txRow">
      <div class="txMain">
        <b>${escapeHtml(t.description || t.category)}</b>
        <small>${formatDate(t.entry_date)} · ${escapeHtml(t.category)}${t.payment_mode ? ' · ' + escapeHtml(t.payment_mode) : ''}</small>
      </div>
      <div class="amount ${isIncome ? 'green' : 'red'}">${isIncome ? '+' : '−'} ${money(t.amount)}</div>
    </div>
  `;
}

function recentTransactionsPreview(tx) {
  const recent = tx.slice(0, 6);
  return `
    <div class="chartCard">
      <h3>Transactions</h3>
      ${recent.length ? `<div class="txList noBorder">${recent.map(combinedTxRow).join('')}</div>` : '<div class="empty">No transactions in this range.</div>'}
      ${tx.length > 6 ? `<button type="button" class="viewAllTxBtn" data-open-report="transactions">View All Transactions →</button>` : ''}
    </div>
  `;
}

function pagerHtml(page, totalPages) {
  if (totalPages <= 1) return '';
  const keep = new Set([1, totalPages, page, page - 1, page + 1]);
  const pages = [...keep].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);

  let html = `<div class="pager"><button type="button" class="pagerBtn" data-page="${page - 1}" ${page <= 1 ? 'disabled' : ''} aria-label="Previous page">‹</button>`;
  let prev = 0;
  for (const p of pages) {
    if (prev && p - prev > 1) html += '<span class="pagerEllipsis">…</span>';
    html += `<button type="button" class="pagerBtn${p === page ? ' active' : ''}" data-page="${p}">${p}</button>`;
    prev = p;
  }
  html += `<button type="button" class="pagerBtn" data-page="${page + 1}" ${page >= totalPages ? 'disabled' : ''} aria-label="Next page">›</button></div>`;
  return html;
}

// ---------------------------------------------------------------------------
// Per-tab bodies -- same underlying data/render logic as before, restyled.
// ---------------------------------------------------------------------------
function renderOverviewReport(tx) {
  const categoryRows = expenseCategoryBreakdown(tx);
  return `
    ${categoryBreakdownCard(categoryRows)}
    ${trendChartCard(tx)}
    ${recentTransactionsPreview(tx)}
  `;
}

function renderMonthlyReport(filteredTx) {
  const rows = monthlyBreakdown(filteredTx);
  return `
    ${monthlyTrendChart(rows)}
    <div class="reportTableWrap"><table class="reportTable">
      <thead><tr><th>Month</th><th class="right">Income</th><th class="right">Expense</th><th class="right">Profit</th><th class="right">Profit %</th></tr></thead>
      <tbody>
        ${rows.length ? rows.map((r) => `
          <tr>
            <td>${monthLabel(r.month)}</td>
            <td class="right green">${money(r.income)}</td>
            <td class="right red">${money(r.expense)}</td>
            <td class="right ${r.profit >= 0 ? 'green' : 'red'}">${money(r.profit)}</td>
            <td class="right">${r.profitPct.toFixed(1)}%</td>
          </tr>
        `).join('') : '<tr><td colspan="5" class="empty">No data in this range.</td></tr>'}
      </tbody>
    </table></div>
  `;
}

function renderCategoryReport(filteredTx) {
  const rows = expenseCategoryBreakdown(filteredTx);
  return categoryBreakdownCard(rows);
}

function renderMaterialReport(filteredMaterials) {
  const total = filteredMaterials.reduce((s, m) => s + Number(m.quantity) * Number(m.rate), 0);
  return `
    ${filteredMaterials.length ? `<div class="reportSummaryLine">Total Material Expense <b class="red">${money(total)}</b></div>` : ''}
    <div class="reportTableWrap"><table class="reportTable">
      <thead><tr><th>Date</th><th>Material</th><th class="right">Qty</th><th>Unit</th><th class="right">Rate</th><th class="right">Amount</th><th>Supplier</th><th>Bill</th><th>Notes</th></tr></thead>
      <tbody>
        ${filteredMaterials.length ? filteredMaterials.map((m) => `
          <tr>
            <td>${formatDate(m.purchase_date)}</td>
            <td>${escapeHtml(m.material_name)}</td>
            <td class="right">${m.quantity}</td>
            <td>${escapeHtml(m.unit)}</td>
            <td class="right">${money(m.rate)}</td>
            <td class="right">${money(m.quantity * m.rate)}</td>
            <td>${escapeHtml(m.supplier || '—')}</td>
            <td>${m.bill_file_path ? '📎' : '—'}</td>
            <td>${escapeHtml(m.notes || '—')}</td>
          </tr>
        `).join('') : '<tr><td colspan="9" class="empty">No material purchases in this range.</td></tr>'}
      </tbody>
      ${filteredMaterials.length ? `<tfoot><tr><td colspan="5"><b>Total Material Expense</b></td><td class="right"><b>${money(total)}</b></td><td colspan="3"></td></tr></tfoot>` : ''}
    </table></div>
  `;
}

function renderLabourReport(filteredLabour) {
  const total = filteredLabour.reduce((s, l) => s + Number(l.amount_paid), 0);
  return `
    ${filteredLabour.length ? `<div class="reportSummaryLine">Total Labour Expense <b class="red">${money(total)}</b></div>` : ''}
    <div class="reportTableWrap"><table class="reportTable">
      <thead><tr><th>Date</th><th>Worker</th><th>Basis</th><th>Days/Sqft/Contract</th><th class="right">Rate</th><th class="right">Amount</th><th>Payment</th><th>Notes</th></tr></thead>
      <tbody>
        ${filteredLabour.length ? filteredLabour.map((l) => `
          <tr>
            <td>${formatDate(l.payment_date)}</td>
            <td>${escapeHtml(l.worker_name)}</td>
            <td>${escapeHtml(l.basis)}</td>
            <td>${escapeHtml(labourBasisValue(l))}</td>
            <td class="right">${labourRateDisplay(l)}</td>
            <td class="right">${money(l.amount_paid)}</td>
            <td>${escapeHtml(l.payment_mode || '—')}</td>
            <td>${escapeHtml(l.notes || '—')}</td>
          </tr>
        `).join('') : '<tr><td colspan="8" class="empty">No labour payments in this range.</td></tr>'}
      </tbody>
      ${filteredLabour.length ? `<tfoot><tr><td colspan="5"><b>Total Labour Expense</b></td><td class="right"><b>${money(total)}</b></td><td colspan="2"></td></tr></tfoot>` : ''}
    </table></div>
  `;
}

function renderIncomeReport(rows) {
  const total = rows.reduce((s, t) => s + Number(t.amount), 0);
  return `
    ${rows.length ? `<div class="reportSummaryLine">Total Income <b class="green">${money(total)}</b></div>` : ''}
    <div class="reportTableWrap"><table class="reportTable">
      <thead><tr><th>Date</th><th>Description</th><th>Category</th><th class="right">Amount</th><th>Payment</th><th>Notes</th></tr></thead>
      <tbody>
        ${rows.length ? rows.map((t) => `
          <tr>
            <td>${formatDate(t.entry_date)}</td>
            <td>${escapeHtml(t.description || '—')}</td>
            <td>${escapeHtml(t.category)}</td>
            <td class="right green">${money(t.amount)}</td>
            <td>${escapeHtml(t.payment_mode || '—')}</td>
            <td>${escapeHtml(t.notes || '—')}</td>
          </tr>
        `).join('') : '<tr><td colspan="6" class="empty">No income in this range.</td></tr>'}
      </tbody>
      ${rows.length ? `<tfoot><tr><td colspan="3"><b>Total Income</b></td><td class="right"><b>${money(total)}</b></td><td colspan="2"></td></tr></tfoot>` : ''}
    </table></div>
  `;
}

function renderExpenseReport(rows) {
  const total = rows.reduce((s, t) => s + Number(t.amount), 0);
  return `
    ${rows.length ? `<div class="reportSummaryLine">Total Expense <b class="red">${money(total)}</b></div>` : ''}
    <div class="reportTableWrap"><table class="reportTable">
      <thead><tr><th>Date</th><th>Category</th><th>Description</th><th class="right">Amount</th><th>Payment</th><th>Notes</th></tr></thead>
      <tbody>
        ${rows.length ? rows.map((t) => `
          <tr>
            <td>${formatDate(t.entry_date)}</td>
            <td>${escapeHtml(t.category)}</td>
            <td>${escapeHtml(t.description || '—')}</td>
            <td class="right red">${money(t.amount)}</td>
            <td>${escapeHtml(t.payment_mode || '—')}</td>
            <td>${escapeHtml(t.notes || '—')}</td>
          </tr>
        `).join('') : '<tr><td colspan="6" class="empty">No expenses in this range.</td></tr>'}
      </tbody>
      ${rows.length ? `<tfoot><tr><td colspan="3"><b>Total Expense</b></td><td class="right"><b>${money(total)}</b></td><td colspan="2"></td></tr></tfoot>` : ''}
    </table></div>
  `;
}

function renderTransactionsReport(state, tx) {
  if (!tx.length) return '<div class="empty">No transactions in this range.</div>';
  const totalPages = Math.max(1, Math.ceil(tx.length / TX_PAGE_SIZE));
  const page = Math.min(Math.max(1, state.reportsTxPage || 1), totalPages);
  const start = (page - 1) * TX_PAGE_SIZE;
  const pageRows = tx.slice(start, start + TX_PAGE_SIZE);
  return `
    <div class="reportTableWrap"><table class="reportTable">
      <thead><tr><th>Date</th><th>Description</th><th>Category</th><th>Type</th><th class="right">Amount</th></tr></thead>
      <tbody>
        ${pageRows.map((t) => `
          <tr>
            <td>${formatDate(t.entry_date)}</td>
            <td>${escapeHtml(t.description || '—')}</td>
            <td>${escapeHtml(t.category)}</td>
            <td>${t.type === 'income' ? 'Income' : 'Expense'}</td>
            <td class="right ${t.type === 'income' ? 'green' : 'red'}">${t.type === 'income' ? '+' : '−'} ${money(t.amount)}</td>
          </tr>
        `).join('')}
      </tbody>
    </table></div>
    ${pagerHtml(page, totalPages)}
  `;
}

function scoped(state) {
  return {
    tx: filterByDateRange(state.recentTransactions, 'entry_date', state.reportDateFrom, state.reportDateTo),
    materials: filterByDateRange(state.materialRecords, 'purchase_date', state.reportDateFrom, state.reportDateTo),
    labour: filterByDateRange(state.labourRecords, 'payment_date', state.reportDateFrom, state.reportDateTo)
  };
}

export function reportsSection(state) {
  const activeKey = state.activeReport || REPORT_DEFS[0].key;

  if (state.recentTransactionsLoading || state.materialRecordsLoading || state.labourRecordsLoading) {
    return `${reportsHeader(state)}<div class="empty">Loading…</div>`;
  }

  const { tx, materials, labour } = scoped(state);
  const def = REPORT_DEFS.find((d) => d.key === activeKey);

  let body = '';
  switch (activeKey) {
    case 'overview': body = renderOverviewReport(tx); break;
    case 'monthly': body = renderMonthlyReport(tx); break;
    case 'category': body = renderCategoryReport(tx); break;
    case 'material': body = renderMaterialReport(materials); break;
    case 'labour': body = renderLabourReport(labour); break;
    case 'income': body = renderIncomeReport(tx.filter((t) => t.type === 'income')); break;
    case 'expense': body = renderExpenseReport(tx.filter((t) => t.type === 'expense')); break;
    case 'transactions': body = renderTransactionsReport(state, tx); break;
    default: body = '';
  }

  return `
    ${reportsHeader(state)}
    ${financialSummarySection(tx)}
    ${tabBar(activeKey)}
    <div class="reportsBody">
      <div class="reportsBodyHead"><h3>${escapeHtml(def?.label || 'Report')}</h3></div>
      ${body}
    </div>
  `;
}

function openCategoryDrilldown(category) {
  const state = getState();
  const { tx } = scoped(state);
  const rows = tx.filter((t) => t.type === 'expense' && t.category === category);
  const body = document.createElement('div');
  body.className = 'txList';
  body.innerHTML = rows.length
    ? rows.map((t) => `
        <div class="txRow">
          <div class="txMain"><b>${escapeHtml(t.description || t.category)}</b><small>${formatDate(t.entry_date)}${t.payment_mode ? ' · ' + escapeHtml(t.payment_mode) : ''}</small></div>
          <div class="amount red">− ${money(t.amount)}</div>
        </div>
      `).join('')
    : '<div class="empty">No transactions in this category.</div>';
  openFormSheet(`${category} — Transactions`, body);
}

function buildReportCSVData(key, state) {
  const { tx, materials, labour } = scoped(state);
  if (key === 'overview' || key === 'transactions') {
    return {
      headers: ['Date', 'Description', 'Category', 'Type', 'Amount', 'Payment Mode', 'Notes'],
      rows: tx.map((t) => [t.entry_date, t.description || '', t.category, t.type === 'income' ? 'Income' : 'Expense', t.amount, t.payment_mode || '', t.notes || ''])
    };
  }
  if (key === 'monthly') {
    const rows = monthlyBreakdown(tx);
    return {
      headers: ['Month', 'Income', 'Expense', 'Profit', 'Profit %'],
      rows: rows.map((r) => [monthLabel(r.month), r.income, r.expense, r.profit, r.profitPct.toFixed(1) + '%'])
    };
  }
  if (key === 'category') {
    const rows = expenseCategoryBreakdown(tx);
    return { headers: ['Category', 'Amount'], rows: rows.map((r) => [r.category, r.amount]) };
  }
  if (key === 'material') {
    return {
      headers: ['Date', 'Material', 'Quantity', 'Unit', 'Rate', 'Amount', 'Supplier', 'Bill', 'Notes'],
      rows: materials.map((m) => [m.purchase_date, m.material_name, m.quantity, m.unit, m.rate, (Number(m.quantity) * Number(m.rate)).toFixed(2), m.supplier || '', m.bill_file_path ? 'Yes' : 'No', m.notes || ''])
    };
  }
  if (key === 'labour') {
    return {
      headers: ['Date', 'Worker', 'Basis', 'Days/Sqft/Contract', 'Rate', 'Amount', 'Payment Mode', 'Notes'],
      rows: labour.map((l) => [l.payment_date, l.worker_name, l.basis, labourBasisValue(l), labourRateRaw(l), l.amount_paid, l.payment_mode || '', l.notes || ''])
    };
  }
  if (key === 'income') {
    return {
      headers: ['Date', 'Description', 'Category', 'Amount', 'Payment Mode', 'Notes'],
      rows: tx.filter((t) => t.type === 'income').map((t) => [t.entry_date, t.description || '', t.category, t.amount, t.payment_mode || '', t.notes || ''])
    };
  }
  if (key === 'expense') {
    return {
      headers: ['Date', 'Category', 'Description', 'Amount', 'Payment Mode', 'Notes'],
      rows: tx.filter((t) => t.type === 'expense').map((t) => [t.entry_date, t.category, t.description || '', t.amount, t.payment_mode || '', t.notes || ''])
    };
  }
  return { headers: [], rows: [] };
}

export function wireReportsScreen(shell) {
  shell.querySelectorAll('[data-open-report]').forEach((btn) => {
    btn.addEventListener('click', () => setState({ activeReport: btn.dataset.openReport, reportsTxPage: 1 }));
  });

  shell.querySelectorAll('[data-report-category]').forEach((row) => {
    row.addEventListener('click', () => openCategoryDrilldown(row.dataset.reportCategory));
  });

  shell.querySelectorAll('.pagerBtn[data-page]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const p = Number(btn.dataset.page);
      if (Number.isFinite(p) && p >= 1) setState({ reportsTxPage: p });
    });
  });

  shell.querySelector('#reportsSitePill')?.addEventListener('click', openSitePicker);
  shell.querySelector('#reportsDatePill')?.addEventListener('click', openDateRangeSheet);

  shell.querySelector('#reportExportBtn')?.addEventListener('click', () => {
    const state = getState();
    const key = state.activeReport || REPORT_DEFS[0].key;
    const { headers, rows } = buildReportCSVData(key, state);
    const csv = toCSV(headers, rows);
    downloadCSV(`afitexp-${key}-${todayISO()}.csv`, csv);
  });
}
